import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal } from 'antd';

import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import VendorAutoComplete from '@totem/components/vendors/vendorAutoComplete/VendorAutoComplete';
import VendorLogoContext from '@totem/components/vendors/vendorLogos/vendorLogoContext';

export type VendorAliasDialogProps = {
  open: boolean;
  onClose: () => void;
  defaultAlias?: string;
};

const VendorAliasDialog = ({
  open,
  onClose,
  defaultAlias,
}: VendorAliasDialogProps) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { addAliasToVendor, rawVendorData } = useContext(VendorLogoContext);
  const [vendorName, setVendorName] = useState<string>('');
  const [alias, setAlias] = useState<string>(defaultAlias || '');
  const [vendorError, setVendorError] = useState<string>('');
  const [aliasError, setAliasError] = useState<string>('');

  const normalizedVendors = useMemo(
    () =>
      (rawVendorData || []).map((vendor) => ({
        name: vendor.name?.trim().toLowerCase() || '',
        aliases: (vendor.aliases || []).map((aliasItem) =>
          aliasItem.trim().toLowerCase(),
        ),
      })),
    [rawVendorData],
  );

  useEffect(() => {
    if (open) {
      setVendorName('');

      setAlias(defaultAlias || '');

      setVendorError('');

      setAliasError('');
    }
  }, [open, defaultAlias]);

  useEffect(() => {
    if (!vendorName) {
      setVendorError('');
    }
  }, [vendorName]);

  useEffect(() => {
    if (!alias) {
      setAliasError('');
    }
  }, [alias]);

  const isAliasDuplicate = (aliasValue: string) => {
    const normalizedAlias = aliasValue.trim().toLowerCase();
    if (!normalizedAlias) {
      return false;
    }

    return normalizedVendors.some(
      (vendor) =>
        vendor.name === normalizedAlias ||
        vendor.aliases.includes(normalizedAlias),
    );
  };

  const handleSubmit = () => {
    const trimmedVendor = vendorName.trim();
    const trimmedAlias = alias.trim();

    let hasError = false;

    if (!trimmedVendor) {
      setVendorError('Vendor is required.');

      hasError = true;
    } else {
      setVendorError('');
    }

    if (!trimmedAlias) {
      setAliasError('Alias is required.');

      hasError = true;
    } else if (isAliasDuplicate(trimmedAlias)) {
      setAliasError('Alias already exists as a vendor name or alias.');

      hasError = true;
    } else {
      setAliasError('');
    }

    if (hasError) {
      return;
    }

    addAliasToVendor(trimmedVendor, trimmedAlias, getApiToken());

    onClose();
  };

  return (
    <Modal
      open={open}
      title="Add Vendor Alias"
      onCancel={onClose}
      onOk={handleSubmit}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item
          label="Vendor"
          validateStatus={vendorError ? 'error' : undefined}
          help={vendorError || undefined}
        >
          <VendorAutoComplete
            value={vendorName}
            onChange={setVendorName}
            requireSelection={false}
            placeholder="Select or type a vendor"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="Alias"
          validateStatus={aliasError ? 'error' : undefined}
          help={aliasError || undefined}
        >
          <Input
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            placeholder="Alias"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default VendorAliasDialog;
